import { EllipsisOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import Menu from '@/components/Menu/Menu';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useDashboard2Store from '@/stores/Dashboard2Store';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';

import useDashboardCardDrag from '../../hooks/useDashboardCardDrag';
import type { DashboardCardProps } from '../DashboardCard';
import DashboardCardStyles from '../styles/DashboardCardStyles';
import { DashboardTextContentStyle } from '../styles/TextContentStyle';
import { trimTrailingEmptyQuillBlocks } from './quill-html';
import SimpleEditor from './SimpleEditor';
const { StyledDragArea, StyledDashboardTextCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

const DashboardTextBox = (props: DashboardCardProps) => {
  const originText = props.column.text;
  if (!originText) throw new Error('Text is not exist');

  const color = useColor();
  const addToast = useToast();
  const setPopup = usePopup();
  const queryClient = useQueryClient();
  const setEditingState = useDashboard2Store((state) => state.setEditingState);
  const { index, rowIndex, setSomeDragging, column, rowId, width } = props;
  const { dragRef, previewRef, isDragging } = useDashboardCardDrag({
    index,
    rowIndex,
    setSomeDragging,
    column,
    rowId,
  });

  const [text, setText] = useState(originText);
  const [editMode, setEditMode] = useState(false);
  const displayText = trimTrailingEmptyQuillBlocks(text);

  const handleEditMode = (state: boolean) => {
    setEditMode(state);
    setEditingState(state);
  };

  const onEditDone = async () => {
    const nextText = trimTrailingEmptyQuillBlocks(text);

    await service.dashboard2.changeDashboardRowText(
      props.dashboardId,
      props.column.id,
      nextText,
    );
    queryClient.invalidateQueries({ queryKey: ['dashboard2', props.dashboardId] });
    handleEditMode(false);
  };

  const onCancel = () => {
    setText(props.column.text || '');
    handleEditMode(false);
  };

  const onDelete = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Remove from dashboard',
      message:
        'Are you sure you want to remove this text box from the dashboard?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await service.dashboard2.deleteColumnInDashboardRow(
            props.dashboardId,
            props.rowId,
            props.column.id,
          );
          // refetch dashboard
          queryClient.invalidateQueries({ queryKey: ['dashboard2', props.dashboardId] });

          addToast('Text box deleted from dashboard', 'success');
        } catch (e) {
          addToast('Error! try again', 'fail');
        }
      },
    });
  };

  if (editMode)
    return (
      <Style style={{ width: width, maxHeight: 800 }} g={8}>
        <SimpleEditor
          value={text}
          onChange={(e) => {
            setText(e);
          }}
          wrapperStyle={{
            backgroundColor: color.gray200,
            padding: '0px 24px 8px 24px',
            borderRadius: 16,
            height: '100%',
            overflow: 'auto',
            flex: 1,
          }}
          textStyleCss={DashboardTextContentStyle}
        />
        <Flex j="space-between">
          <Button variant="error_outlined" onClick={onDelete}>
            Delete
          </Button>
          <Flex g={8} j="flex-end">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onEditDone}>Done</Button>
          </Flex>
        </Flex>
      </Style>
    );

  return (
    <StyledDashboardCardWrapper
      isTextCard={true}
      ref={previewRef}
      isDragging={isDragging}
      width={width}
    >
      <StyledDashboardTextCard>
        <StyledDragArea
          ref={dragRef}
          className="drag-area-handler"
          style={{ top: 6 }}
        >
          <HandlerIcon />
        </StyledDragArea>
        <Transition
          status={true}
          css={css`
            position: relative;
            display: flex;
            flex-direction: row;
            height: 100%;
            padding: 16px 0px 16px 16px;
            overflow: auto;
            .ellipsis-button {
              opacity: 0;
              position: absolute;
              right: 24px;
              top: 24px;
              background-color: ${color.gray100};
            }
            &:hover {
              .ellipsis-button {
                opacity: 1;
              }
            }
          `}
          onClick={() => {
            handleEditMode(true);
          }}
        >
          <TextDisplay
            className="ql-editor"
            style={{ flex: 1 }}
            dangerouslySetInnerHTML={{
              __html: displayText,
            }}
          />
          <Menu
            triggerNode={
              <IconButton
                icon={<EllipsisOutlined />}
                sizeType="m"
                className="ellipsis-button"
              />
            }
            options={[
              {
                label: 'Edit',
                onClick: () => {
                  handleEditMode(true);
                },
              },
              {
                label: 'Delete',
                onClick: onDelete,
              },
            ]}
          />
        </Transition>
      </StyledDashboardTextCard>
    </StyledDashboardCardWrapper>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  flex-shrink: 1;
  min-height: 140px;
  overflow: auto;
  padding: 0px 12px;
`;

const TextDisplay = styled.div`
  ${DashboardTextContentStyle};
`;

export default DashboardTextBox;
